import type { ConfigResponse, LeadPayload, ReplyResponse, WidgetConfig } from "./types";

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message = payload?.error?.message ?? "Une erreur est survenue.";
    throw new ApiError(message, response.status);
  }

  return payload as T;
}

export async function fetchChatConfig(config: WidgetConfig): Promise<ConfigResponse> {
  const url = new URL(`${config.apiBaseUrl}/chat-config`);
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("page_url", window.location.href);
  url.searchParams.set("origin", window.location.origin);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json"
    }
  });

  return parseResponse<ConfigResponse>(response);
}

export async function sendQuickReply(params: {
  config: WidgetConfig;
  chatbotId: string;
  visitorId: string;
  conversationId: string | null;
  quickReplyId: string;
}): Promise<ReplyResponse> {
  const response = await fetch(`${params.config.apiBaseUrl}/chat-quick-reply`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      client_id: params.config.clientId,
      chatbot_id: params.chatbotId,
      conversation_id: params.conversationId,
      visitor_id: params.visitorId,
      quick_reply_id: params.quickReplyId,
      page_url: window.location.href
    })
  });

  return parseResponse<ReplyResponse>(response);
}

export async function sendMessage(params: {
  config: WidgetConfig;
  chatbotId: string;
  visitorId: string;
  conversationId: string | null;
  message: string;
}): Promise<ReplyResponse> {
  const response = await fetch(`${params.config.apiBaseUrl}/chat-message`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      client_id: params.config.clientId,
      chatbot_id: params.chatbotId,
      conversation_id: params.conversationId,
      visitor_id: params.visitorId,
      message: params.message,
      page_url: window.location.href
    })
  });

  return parseResponse<ReplyResponse>(response);
}

export async function submitLead(params: {
  config: WidgetConfig;
  chatbotId: string;
  conversationId: string;
  lead: LeadPayload;
}): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${params.config.apiBaseUrl}/chat-lead`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      client_id: params.config.clientId,
      chatbot_id: params.chatbotId,
      conversation_id: params.conversationId,
      name: params.lead.name,
      email: params.lead.email,
      phone: params.lead.phone,
      need: params.lead.need,
      page_url: window.location.href
    })
  });

  return parseResponse<{ success: boolean; message: string }>(response);
}
